'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toProvenanceLookupPath } from '@/shared/functions';
import type { ScopesObject } from '@/shared/types';
import { useSyncEvidenceFileTypeFiltersToScopes } from './use-evidence-file-type-filters-new';

export function emptyScopesNew(): ScopesObject {
  return {
    scopes: [],
    files: [],
    excludedScopes: [],
    excludedFiles: [],
    contentCategories: [],
    excludedContentCategories: [],
    evidenceCategoryChips: [],
    excludedEvidenceCategoryScopes: [],
    evidenceCategoryScopes: [],
    evidenceAllFileTypes: false,
  };
}

export type ChatEvidenceLockerContextValue = {
  caseId: string;
  scopesAndFiles: ScopesObject;
  setScopesAndFiles: (
    update: ScopesObject | ((prev: ScopesObject) => ScopesObject),
  ) => void;
  composerQuery: string;
  setComposerQuery: (q: string) => void;
  showFolders: boolean;
  setShowFolders: React.Dispatch<React.SetStateAction<boolean>>;
  allFileTypes: boolean;
  setAllFileTypes: React.Dispatch<React.SetStateAction<boolean>>;
  activeFileTypes: Record<string, boolean>;
  setActiveFileTypes: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  addSourceToScope: (path: string) => void;
  /**
   * Latest request to open the locker on a folder path. `id` increments so the
   * same path can be requested again. Null until the first request.
   * `container` is set when the path is inside a zip/mbox so browse can send
   * that file's id; a plain path does not resolve synthetic folders.
   */
  browseRequest: LockerBrowseRequest | null;
  /** Open the locker (when an opener is registered) and browse to `path`. */
  requestBrowse: (path: string, container?: LockerBrowseContainer | null) => void;
  /** Question bar registers the function that opens the locker modal. */
  registerLockerOpener: (opener: (() => void) | null) => void;
};

export type LockerBrowseContainer = {
  searchablePath: string;
  fileStatusId: string;
};

export type LockerBrowseRequest = {
  id: number;
  path: string;
  container?: LockerBrowseContainer | null;
};

export const ChatEvidenceLockerContext = createContext<
  ChatEvidenceLockerContextValue | undefined
>(undefined);

export type ChatEvidenceLockerProviderProps = {
  caseId: string;
  initialScopes?: ScopesObject;
  children: ReactNode;
  /** Parent-owned state (legacy ``Chat`` + ``QuestionBar`` bridge). */
  scopesAndFiles?: ScopesObject;
  setScopesAndFiles?: ChatEvidenceLockerContextValue['setScopesAndFiles'];
  showFolders?: boolean;
  setShowFolders?: React.Dispatch<React.SetStateAction<boolean>>;
  allFileTypes?: boolean;
  setAllFileTypes?: React.Dispatch<React.SetStateAction<boolean>>;
  activeFileTypes?: Record<string, boolean>;
  setActiveFileTypes?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
};

export function ChatEvidenceLockerProvider({
  caseId,
  initialScopes,
  children,
  scopesAndFiles: controlledScopes,
  setScopesAndFiles: controlledSetScopes,
  showFolders: controlledShowFolders,
  setShowFolders: controlledSetShowFolders,
  allFileTypes: controlledAllFileTypes,
  setAllFileTypes: controlledSetAllFileTypes,
  activeFileTypes: controlledActiveFileTypes,
  setActiveFileTypes: controlledSetActiveFileTypes,
}: ChatEvidenceLockerProviderProps) {
  const isControlled =
    controlledScopes !== undefined && controlledSetScopes !== undefined;

  const [internalScopes, setInternalScopesState] = useState<ScopesObject>(
    () => initialScopes ?? emptyScopesNew(),
  );
  const [composerQuery, setComposerQuery] = useState('');
  const [internalShowFolders, setInternalShowFolders] = useState(false);
  const [internalAllFileTypes, setInternalAllFileTypes] = useState(true);
  const [internalActiveFileTypes, setInternalActiveFileTypes] = useState<
    Record<string, boolean>
  >({});

  const scopesAndFiles = isControlled ? controlledScopes! : internalScopes;
  const showFolders = isControlled
    ? (controlledShowFolders ?? false)
    : internalShowFolders;
  const setShowFolders = isControlled
    ? (controlledSetShowFolders ?? setInternalShowFolders)
    : setInternalShowFolders;
  const allFileTypes = isControlled
    ? (controlledAllFileTypes ?? true)
    : internalAllFileTypes;
  const setAllFileTypes = isControlled
    ? (controlledSetAllFileTypes ?? setInternalAllFileTypes)
    : setInternalAllFileTypes;
  const activeFileTypes = isControlled
    ? (controlledActiveFileTypes ?? {})
    : internalActiveFileTypes;
  const setActiveFileTypes = isControlled
    ? (controlledSetActiveFileTypes ?? setInternalActiveFileTypes)
    : setInternalActiveFileTypes;

  const setInternalScopes = useCallback(
    (update: ScopesObject | ((prev: ScopesObject) => ScopesObject)) => {
      setInternalScopesState((prev) =>
        typeof update === 'function' ? update(prev) : update,
      );
    },
    [],
  );

  const setScopesAndFiles = isControlled
    ? controlledSetScopes!
    : setInternalScopes;

  const openerRef = useRef<(() => void) | null>(null);
  const browseIdRef = useRef(0);
  const [browseRequest, setBrowseRequest] = useState<LockerBrowseRequest | null>(null);
  const registerLockerOpener = useCallback((opener: (() => void) | null) => {
    openerRef.current = opener;
  }, []);
  const requestBrowse = useCallback((path: string, container?: LockerBrowseContainer | null) => {
    browseIdRef.current += 1;
    setBrowseRequest({ id: browseIdRef.current, path, container: container ?? null });
    openerRef.current?.();
  }, []);

  const addSourceToScope = useCallback(
    (path: string) => {
      const normalized = toProvenanceLookupPath(path);
      if (!normalized) return;
      setScopesAndFiles((prev) => ({
        ...prev,
        files: Array.from(new Set([...prev.files, normalized])),
      }));
    },
    [setScopesAndFiles],
  );

  const value = useMemo<ChatEvidenceLockerContextValue>(
    () => ({
      caseId,
      scopesAndFiles,
      setScopesAndFiles,
      composerQuery,
      setComposerQuery,
      showFolders,
      setShowFolders,
      allFileTypes,
      setAllFileTypes,
      activeFileTypes,
      setActiveFileTypes,
      addSourceToScope,
      browseRequest,
      requestBrowse,
      registerLockerOpener,
    }),
    [
      caseId,
      scopesAndFiles,
      setScopesAndFiles,
      composerQuery,
      setComposerQuery,
      showFolders,
      setShowFolders,
      allFileTypes,
      setAllFileTypes,
      activeFileTypes,
      setActiveFileTypes,
      addSourceToScope,
      browseRequest,
      requestBrowse,
      registerLockerOpener,
    ],
  );

  return (
    <ChatEvidenceLockerContext.Provider value={value}>
      <SyncEvidenceFileTypeFiltersToScopes />
      {children}
    </ChatEvidenceLockerContext.Provider>
  );
}

function SyncEvidenceFileTypeFiltersToScopes() {
  useSyncEvidenceFileTypeFiltersToScopes();
  return null;
}

export function useChatEvidenceLockerNew(): ChatEvidenceLockerContextValue {
  const ctx = useContext(ChatEvidenceLockerContext);
  if (!ctx) {
    throw new Error(
      'useChatEvidenceLockerNew must be used within ChatEvidenceLockerProvider',
    );
  }
  return ctx;
}
